import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';

import { Order } from '../../../models/order.model';
import { OrderTypes } from '../../../enums/order-types';
import { OrderService } from '../../../services/order.service';
import { AuthService } from '../../../services/auth.service';
import { ORDER_NOTES_INFO } from '../../../constants/texts';

@UntilDestroy()
@Component({
  selector: 'app-order-settings-modal',
  templateUrl: './order-settings-modal.component.html',
  styleUrls: ['./order-settings-modal.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class OrderSettingsModalComponent implements OnInit {
  orderSettingsForm: FormGroup;
  label: String;

  public orderName: AbstractControl;
  public notes: AbstractControl;
  public poNumber: AbstractControl;
  public taxExempt: FormControl;
  public attention: AbstractControl;
  isAttentionChanged = false;

  inProcess = false;
  order: Order;

  constructor(
    private formBuilder: FormBuilder,
    private orderService: OrderService,
    private authService: AuthService,
    private activeModal: NgbActiveModal
  ) {}

  ngOnInit(): void {
    this.orderSettingsForm = this.formBuilder.group({
      orderName: ['', Validators.compose([Validators.required])],
      notes: ['', Validators.compose([])],
      poNumber: ['', Validators.compose([])],
      attention: ['', Validators.compose([])],
      taxExempt: ['', Validators.compose([])],
    });
    this.orderName = this.orderSettingsForm.controls['orderName'];
    this.notes = this.orderSettingsForm.controls['notes'];
    this.poNumber = this.orderSettingsForm.controls['poNumber'];
    this.attention = this.orderSettingsForm.controls['attention'];
    this.taxExempt = this.orderSettingsForm.controls[
      'taxExempt'
    ] as FormControl;

    this.showDialog(this.order);
  }

  showDialog(order: Order): void {
    const orderName = this.order.cartName;
    this.orderName.setValue(orderName);
    this.notes.setValue(order.attr_notes || '');
    if (order.isAttentionOverridden) {
      this.attention.setValue('');
    } else {
      this.attention.setValue(order.attr_attention || '');
    }
    this.poNumber.setValue(order.attr_po_num || '');
    this.taxExempt.setValue(order.taxExempt || false);

    if (this.isTaxExemptAllowed === false) {
      this.taxExempt.disable();
    } else {
      this.taxExempt.enable();
    }
  }

  saveOrderName() {
    this.order.attr_cart_name = this.orderName.value;
    this.order.attr_po_num = this.poNumber.value;
    this.order.attr_notes = this.notes.value;

    const attrs = {
      cart_name: this.order.attr_cart_name,
      po_num: this.order.attr_po_num,
      notes: this.order.attr_notes,
    };

    if (this.isAttentionChanged) {
      this.order.attr_attention = this.attention.value || '';
      attrs['attention'] = this.order.attr_attention;
    }

    if (this.isTaxExemptAllowed) {
      this.order.taxExempt = this.taxExempt.value;
      attrs['tax_exempt'] = this.order.taxExempt.toString();
    }

    // update cost center label
    // this.order.costCenter.label = this.orderName.value;
    this.inProcess = true;
    this.orderService
      .setOrderAttributes(this.order, attrs)
      .pipe(untilDestroyed(this))
      .subscribe((data) => {
        this.inProcess = false;
        this.close(data);
      });
  }

  clearManageCartForm() {
    this.orderSettingsForm.reset();
  }

  get isEmployee(): boolean {
    return this.authService.isEmployee;
  }

  get canEditSap(): boolean {
    return this.authService.canEditSAP;
  }

  get canShowTaxExempt(): boolean {
    return this.authService.isWholeSaler || this.order.isWSLR;
  }

  get isTaxExemptAllowed(): boolean {
    return this.authService.user.isTaxExemptAllowed || this.order.isWSLR;
  }

  onClosed() {
    this.clearManageCartForm();
  }

  close(next: Order | string = null) {
    this.activeModal.close(next);
  }

  get orderLabel() {
    return this.order && this.order.type === OrderTypes.ECOMMERCE_TYPE
      ? 'Cart'
      : 'Order';
  }

  get orderIntoPlaceHolder(): string {
    return ORDER_NOTES_INFO;
  }
}
